/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewEncapsulation } from '@angular/core';
import { BadgeOptions, badgeColorCode } from '../../badge/badge.interface';
import { NotesOptions, ParamKey } from '../../notes/notes.interface';
import { CustomFieldGroupService } from '../../../core/services/custom_fields_group/custom_fields_group.service';
import { DataCollectorService } from '../../../core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from '../../../core/services/localisation/zuper.localisation.service';
import { UtilityService } from '../../../core/services/utility_functions/utility_functions.service';
import { forkJoin } from 'rxjs';
import { NotesService } from '../../../core/services/notes/notes.service';
import { moduleKey } from '../../../core/constants/constants';
import { JobsService } from '../../../core/services/jobs/jobs.service';
import { rrulestr } from 'rrule';
import { ViewAllRecurringJobsComponent } from '../../../components/view-all-recurring-jobs/view-all-recurring-jobs.component';
import { ZuperDialogService } from '@zuper/components/zuper-dialog/zuper-dialog.service';
import { DateTimePipe } from '../../../core/pipes/datetimeformator.pipe';
import { CustomFieldsOptions } from '../../../components/custom-fields/custom-fields.interface';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { LocalStorageService } from '../../../core/services/localstorage.service';
import { NgxTippyProps, NgxTippyService } from 'ngx-tippy-wrapper';
import _ from 'lodash';
import { PreviewMenuOptions } from '../preview-data.interface';
import moment, { Moment } from 'moment';
import { ProjectGanttService } from 'app/modules/projects/project-jobs/gantt/project-gantt.service';
import { ProjectsService } from 'app/core/services/project/projects.service';

@Component({
  selector: 'job-summary-preview',
  templateUrl: './job-summary-preview.component.html',
  styleUrls: ['./job-summary-preview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection:ChangeDetectionStrategy?.OnPush
})
export class JobSummaryPreviewComponent implements OnInit, OnChanges {
  @Input() jobId: any = '';
  @Input() allowToEdit: boolean = false;
  @Input() menuOptions: PreviewMenuOptions = null;
  @Input() timeZone: string;
  @Input() trackEventOptions?: any;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Output() jobUpdated: EventEmitter<any> = new EventEmitter();

  object = Object;
  customFieldGroups: any = {};
  masterCustomFields: any[] = [];
  projectDetails: any;
  currentJobPhase = null;
  totalJobDependencyCount = 0;
  loaded: boolean = false;
  jobDetails: any = {};
  tabs: any[] = [{
    displayName: 'Primary Details',
    displayKey: 'text.primary_details',
    dataKey: 'PrimaryDetails',
    rel: 'job_primary_details'
  }, {
    displayName: 'Notes',
    displayKey: 'text.notes',
    dataKey: 'Notes',
    rel: 'job_notes'
  }];
  viewMode: string = 'PrimaryDetails';
  notesOptions: NotesOptions = null;
  notesCount: number = 0;
  priorityTypes: any[] = [{
    priorityType: 'Low',
    priorityKey: 'LOW'
  }, {
    priorityType: 'Medium',
    priorityKey: 'MEDIUM'
  }, {
    priorityType: 'High',
    priorityKey: 'HIGH'
  }, {
    priorityType: 'Urgent',
    priorityKey: 'URGENT'
  }];
  jobFormGroup: UntypedFormGroup;
  priorityEnabled: boolean = false;
  dueDateEnabled: boolean = false;
  tagsEnabled: boolean = false;
  assignEmployeeTippyProps: NgxTippyProps = {
    allowHTML: true,
    theme: 'light',
    trigger: 'click',
    hideOnClick: false,
    interactive: true,
    placement: 'left',
    appendTo: document.body
  };
  isJobUpdated: boolean = false;
  tagsOption: any = {
    module: 'JOB'
  };
  tags: any[] = [];
  today: Moment;
  moduleAccess = { project: this.utilityService.hasModuleAccess('PROJECT') };
  projectName: string = '';
  hasPermissionToCloneJob: boolean;
  isUserRoleEligible: boolean;
  hasPermissionToDeleteJob: boolean;
  hasCustomerModuleAccess: boolean;
  hasOrganizationModuleAccess: boolean;
  hasPermissionToUpdateJob: boolean;
  hasPermissionToAssignJob: boolean;
  timeFormat: string = '';
  serviceTerritory: any;
  companyConfig: any;
  companyPolicy: any;

  constructor(
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private customFieldServie: CustomFieldGroupService,
    private jobService: JobsService,
    private dateTimePipe: DateTimePipe,
    private notesService: NotesService,
    private toast: HotToastService,
    public tippyService: NgxTippyService,
    private localStorageService: LocalStorageService,
    private zuperDialogService: ZuperDialogService,
    private ganttService: ProjectGanttService,
    private projectsService: ProjectsService,
    private utilityService: UtilityService,
    private changeDetectorRef: ChangeDetectorRef) {
    this.jobFormGroup = new UntypedFormGroup({
      priority: new UntypedFormControl(null),
      dueDate: new UntypedFormControl(null),
      tags: new UntypedFormControl([])
    });
    this.changeDetectorRef.markForCheck();
  }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    this.companyConfig = this.utilityService.companyConfig;
    this.companyPolicy = this.utilityService.companyPolicy;
    this.getJobDetails();
    this.today = moment();
    this.timeZone = this.timeZone ?? this.utilityService.timeZone;
    this.timeFormat = `${this.timeZone} z`;
    this.hasPermissionToCloneJob = this.utilityService.hasPermission('job.clone');
    this.isUserRoleEligible = ['TEAM_LEADER','ADMIN'].includes(this.localStorageService.getLocalStorage('user')?.role?.role_key);
    this.hasPermissionToDeleteJob = this.utilityService.hasPermission('job.delete');
    this.hasCustomerModuleAccess = this.utilityService.hasModuleAccess('CUSTOMER');
    this.hasOrganizationModuleAccess = this.utilityService.hasModuleAccess('ORGANIZATION');
    this.hasPermissionToUpdateJob = this.utilityService.hasPermission('job.update');
    this.hasPermissionToAssignJob = this.utilityService.hasPermission('job.update_assignment');
    this.changeDetectorRef.markForCheck();
  }

  changeViewMode(mode: string): void {
    if (this.viewMode !== mode) {
      this.viewMode = mode;
      this.priorityEnabled = false;
      this.dueDateEnabled = false;
      this.tagsEnabled = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  getBadgeOptions(): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: 'PRIMARY',
      displayKey: '',
      displayName: this.notesCount,
      displayType: 'listingCount'
    };
    return badgeOption;
  }

  closeDrawer(): void {
    let emitValue: any = { drawerClose: true };
    if (this.isJobUpdated) {
      emitValue = { ...emitValue, action: 'UPDATE', uid: this.jobId, job: this.jobDetails };
    }
    this.eventEmitter.emit(emitValue);
    this.changeDetectorRef.markForCheck();
  }

  getJobDetails(): void {
    this.loaded = false;
    forkJoin([
      this.dataCollectorService.getData(this.jobService.getJobUrl(this.jobId)),
      this.dataCollectorService.getData(this.customFieldServie.getModuleCustomFieldsUrl('JOB'))])
      .subscribe(
        ([jobDetails, customFields]: any) => {
          this.jobDetails = jobDetails.data;
          this.projectDetails = this.ganttService.projectDetails || null;
          if (this.projectDetails) {
            this.checkDependency(this.jobDetails);
            for (const phase of this.projectDetails?.project_phases) {
              if (phase.phase_items.some(item => item.job_uid === this.jobDetails.job_uid)) {
                this.currentJobPhase = phase;
              }
            }
          }
          this.masterCustomFields = customFields.data;
          if(this.jobDetails.job_timezone){
            this.timeZone = this.jobDetails.job_timezone;
            this.timeFormat = `${this.timeZone} z`;
          }
          this.updateFormValue();
          this.loaded = true;
          this.getCustomFieldGroups();
          this.notesOptions = this.getNotesOptions();
          if(this.isServiceTerritoryEnabled){
            this.getServiceTerritory();
          }
          this.changeDetectorRef.markForCheck();
        }, (_error: any) => {
          this.loaded = true;
          this.changeDetectorRef.markForCheck();
        });
  }

  get isServiceTerritoryEnabled(): boolean{
    return this.companyConfig?.service_territory?.is_enabled ?? true;
  }

  getServiceTerritory(): any{
    this.jobService.getTerritoryConflicts(this.jobDetails?.job_uid).subscribe((response: any)=>{
      this.serviceTerritory = response.data;
    });
  }

  checkDependency(jobDetails: any): any {
    const payload = {
      dependency: {
        job_uid: jobDetails.job_uid,
        "type": "START"
      }
    };
    this.projectsService.checkDependency(this.projectDetails?.project_uid, payload).subscribe((response: any) => {
    this.totalJobDependencyCount = response.data.dependencies.length;
    this.changeDetectorRef.detectChanges();
    }, (error: any) => {
      this.toast.close(error.message);
    });
  }

  showDependencies(): any{
    this.jobDetails['taskType'] = 'JOB';
    this.closeDrawer();
    this.ganttService.updatedProjectDetails.emit({action: 'UPDATEDEPENDENCY', data: this.jobDetails, isFromPreview: true});
  }

  getAssignedUsers(): any[] {
    return this.jobDetails.assigned_to?.map(value => value.user) || [];
  }

  getcustomFieldOptions(data: any): CustomFieldsOptions {
    return {
      data: data,
      displayInColumn: 1,
      extraClass: 'px-0',
      labelAlign: 'Left'
    };
  }

  getNotesOptions(): NotesOptions {
    const notesOptions: NotesOptions = {
      config: {
        attachments: {
          isEnabled: true,
          attachmentType: 'JOB_NOTE'
        },
        sortOptions: {
          isEnabled: true
        },
        showNotifyUsers: true,
        showUserProfile: true,
        enableNewNote: true,
        enableNewAssociation: false,
        enablePrivacy: true,
        editorOptions: {
          quickCreate: this.utilityService.hasPermission('job.new_note')? false: true,
          allowMentions: true
        },
        renderSmallScreen: true,
        initUponFocus: false
      },
      dataUrl: this.notesService.notesUrl,
      module: moduleKey.JOB,
      moduleUID: this.jobDetails.job_uid,
      params: {
        'sort': 'DESC',
        'sort_by': 'created_at'
      },
      fileUploaderOptions: {
        multiple: true,
        uploadFileFor:'NOTE',
        attachmentType: 'JOB_NOTE',
        showProgress: true,
        showPreview: true,
        uploadDetails: true,
        allowFileTypes: this.utilityService.notesSupportedFileTypes,
        header: {
          isHeader: true,
          headerDisplayKey: 'add.note.attachment.text',
          headerDisplayName: 'Add Note Attachments'
        },
        description: {
          descriptionKey: 'notes.panel.description',
          descriptionName: 'Supported Files: Image, Video, Audio, PDF, DOC, XLS, MSG, EML, Zip'
        },
        close: {
          isClose: true,
          closeDisplayKey: 'close.text',
          closeDisplayName: 'Close'
        }
      },
      defaultAssociatedModuleDetails: this.jobDetails,
      trackEventOptions: this.trackEventOptions
    };
    notesOptions.params['filter.' + ParamKey.JOB] = this.jobDetails.job_uid;
    return notesOptions;
  }

  getCustomFieldGroups(): void {
    this.customFieldGroups = this.customFieldServie.getCustomFieldGroups(this.masterCustomFields, this.jobDetails.custom_fields);
    this.changeDetectorRef.markForCheck();
  }

  getRedirectLink(): string {
    return this.utilityService.getLink('JOB', this.jobDetails.job_uid);
  }

  notesEventHandler(event: any): void {
    if (event && !this.utilityService.isEmptyOrUndefined(event.notesCount)) {
      this.notesCount = event.notesCount;
      this.changeDetectorRef.markForCheck();
    }
  }

  getDateTimeValue(value: any): string {
    return value ? this.dateTimePipe.transformFormatedUTC(value, 'YYYY-MM-DD HH:mm:ss') : null;
  }

  getParsedRRuleValue(rrule: string): string {
    return rrulestr(rrule).toText();
  }

  viewRecurringJobs(data: any): void {
    if (data?.recurring_job_uid) {
      this.zuperDialogService.openCustom(ViewAllRecurringJobsComponent,
        {
          extraClass: 'recurring-job-popup',
          data: data,
          timezone: this.jobDetails?.job_timezone
        });
    }
  }

  getJobStatusOptions(status: any): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: status?.status_name,
      color: status?.status_color || badgeColorCode[status?.status_type],
      displayName: status?.status_name,
      extraClass: 'capitalize'
    };
    return badgeOption;
  }

  getTagBadgeOptions(tag: string, allowCapitalize?: boolean, color?: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: 'TAG',
      color: color || badgeColorCode[tag],
      displayKey: allowCapitalize ? tag?.toLowerCase() : tag,
      displayName: allowCapitalize ? tag?.toLowerCase() : tag,
      extraClass: allowCapitalize ? 'capitalize' : null
    };
    return badgeOption;
  }

  getRoundedTagBadgeOptions(tag: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: 'ROUNDEDTAG',
      displayKey: tag,
      displayName: tag,
      displayType: 'roundedTags',
      extraClass: 'text-base border rounded'
    };
    return badgeOption;
  }

  //#region inline Edit
  updateFormValue(): void {
    if (this.jobDetails) {
      this.jobFormGroup.patchValue({
        priority: this.jobDetails.job_priority ?? null,
        dueDate: this.jobDetails.due_date ? moment(this.jobDetails.due_date).tz(this.timeZone) : null,
        tags: this.jobDetails.job_tags ?? []
      });
      this.setProjectDetails();
      this.changeDetectorRef.markForCheck();
    }
  }

  setProjectDetails(): void {
    if (this.moduleAccess.project) {
      const projectDetails: any = this.jobDetails?.project;
      if (projectDetails) {
        this.projectName = `#${projectDetails?.project_prefix || ''}${projectDetails?.project_number || ''} ${projectDetails?.project_name || ''}`;
        this.changeDetectorRef.markForCheck();
      }
      else {
        this.projectName = '---';
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  editPriority(_event: any): void {
    this.priorityEnabled = true;
    this.changeDetectorRef.markForCheck();
  }

  priorityModeChanged(mode: string): void {
    if (mode === 'edit') {
      this.priorityEnabled = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  resetPriorityField(): void {
    this.priorityEnabled = false;
    this.jobFormGroup.get('priority').setValue(this.jobDetails.job_priority || null);
    this.changeDetectorRef.markForCheck();
  }

  updatePriorityField(): void {
    this.priorityEnabled = false;
    if (this.jobFormGroup.value?.priority && this.jobDetails?.job_priority !== this.jobFormGroup.value?.priority) {
      const updateDetails: any = { 'job_priority': this.jobFormGroup.value?.priority };
      if (this.jobDetails?.is_recurrence) {
        this.openRecurrenceConfirmationBox(updateDetails);
        this.changeDetectorRef.markForCheck();
      } else {
        this.updateJobDetails(updateDetails);
        this.changeDetectorRef.markForCheck();
      }
    }
    this.changeDetectorRef.markForCheck();
  }
  
  openJobInNewTab(): void {
    window.open(this.getRedirectLink(), '_blank');
  }

  openRecurrenceConfirmationBox(updateDetails: any): void {
    const updateJob = this.zuperDialogService.open({
      'title': `${this.utilityService.getLocaleValue('text.update_priority')}`,
      'type': 'error',
      'message': `${this.utilityService.getLocaleValue('job.preview.priority.confirm_message')} ?`,
      'actions': {
        'confirm': {
          'show': true,
          'label': 'Yes',
          'color': 'error',
          'confirmDisplayKey': 'yes',
        },
        'cancel': {
          'show': true,
          'label': 'No',
          'cancelDisplayKey': 'No'
        }
      },
      'dismissible': false,
    });
    updateJob.afterClosed().subscribe((result) => {
      const isUpdateAllRecurrenceJobs: boolean = result ? true : false;
      this.changeDetectorRef.markForCheck();
      this.updateJobDetails(updateDetails, isUpdateAllRecurrenceJobs);
    });
  }

  editDueDate(_event: any): void {
    this.dueDateEnabled = true;
    this.changeDetectorRef.markForCheck();
  }

  dueDateModeChanged(mode: string): void {
    if (mode === 'edit') {
      this.dueDateEnabled = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  hasSameDueDateValue(): boolean {
    return (moment(this.jobDetails.due_date).tz(this.timeZone)?.format() === this.jobFormGroup.value?.dueDate?.format());
  }

  resetDueDateField(): void {
    this.dueDateEnabled = false;
    this.jobFormGroup.get('dueDate').setValue(this.jobDetails.due_date ? moment(this.jobDetails.due_date).tz(this.timeZone) : null);
    this.changeDetectorRef.markForCheck();
  }

  updateDueDateField(): void {
    this.dueDateEnabled = false;
    if (this.jobFormGroup.value?.dueDate && !this.hasSameDueDateValue()) {
      this.updateJobDetails({ 'due_date': this.jobFormGroup.value?.dueDate ? moment(this.jobFormGroup.value?.dueDate).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss') : null });
      this.changeDetectorRef.markForCheck();
    }
    this.changeDetectorRef.markForCheck();
  }

  editTags(_event: any): void {
    this.tagsEnabled = true;
    this.tagsOption['fieldValue'] = this.jobFormGroup.value.tags;
    this.tags = [...this.jobFormGroup.value.tags];
    this.changeDetectorRef.markForCheck();
  }

  tagsModeChanged(mode: string): void {
    if (mode === 'edit') {
      this.tagsEnabled = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  resetTagsField(): void {
    this.tagsEnabled = false;
    this.jobFormGroup.get('tags').setValue(this.jobDetails.job_tags || []);
    this.changeDetectorRef.markForCheck();
  }

  updateTagsField(): void {
    this.tagsEnabled = false;
    this.jobFormGroup.get('tags').setValue(this.tags || []);
    if (this.jobFormGroup.value?.tags && !_.isEqual(this.jobDetails?.job_tags, this.jobFormGroup.value?.tags)) {
      this.updateJobDetails({ 'job_tags': this.jobFormGroup.value?.tags });
    }
    this.changeDetectorRef.markForCheck();
  }

  changeTags(event: any): void {
    this.tags = [...event];
    this.changeDetectorRef.markForCheck();
  }

  updateJobDetails(updateDetails: any, isUpdateAllJobs?: boolean): void {
    const payLoad: any = {
      'job': {
        'job_uid': this.jobDetails.job_uid
      },
      'update_all_jobs': isUpdateAllJobs ?? false
    };
    if (updateDetails && Object.keys(updateDetails).length) {
      payLoad['job'] = { ...payLoad['job'], ...updateDetails };
    }
    this.loaded = false;
    this.toast.loading('Updating Job Details', { id: 'job_updating' });
    this.dataCollectorService.putData(this.jobService.jobAPIUrls.jobsUrl, payLoad).subscribe((response: any) => {
      this.toast.close('job_updating');
      this.toast.success(response.message);
      this.getJobDetails();
      this.loaded = true;
      this.isJobUpdated = true;
      this.jobUpdated.emit({ isJobUpdated: true, uid: this.jobId });
      this.changeDetectorRef.markForCheck();
    }, (error: any) => {
      this.loaded = true;
      this.toast.close('job_updating');
      this.changeDetectorRef.markForCheck();
    });
  }
  //#endregion

  //#region More Actions
  cloneJob(_event: any): void {
    this.eventEmitter.emit({ drawerClose: true, action: 'CLONE', uid: this.jobId, job: this.jobDetails, isRecurrence: this.jobDetails.is_recurrence });
    this.changeDetectorRef.markForCheck();
  }

  deleteJob(_event: any): void {
    const deleteJob = this.zuperDialogService.open({
      'title': `${this.utilityService.getLocaleValue('text.delete_job')}`,
      'type': 'error',
      'message': `${this.utilityService.getLocaleValue('delete.job.confirmation.message')} '${this.jobDetails?.job_title}'?`,
      'actions': {
        'confirm': {
          'show': true,
          'label': 'Delete',
          'color': 'error',
          'confirmDisplayKey': 'delete.text',
        },
        'cancel': {
          'show': true,
          'label': 'Cancel',
          'cancelDisplayKey': 'cancel.text'
        }
      },
      'dismissible': false,
    });
    deleteJob.afterClosed().subscribe((result) => {
      if (result) {
        this.toast.loading(`${this.utilityService.getLocaleValue('job_details.deleting.message')}`, { id: 'deleting_job' });
        this.dataCollectorService.deleteData(this.jobService.deleteURL(this.jobId)).subscribe((response: any) => {
          this.toast.close('deleting_job');
          this.toast.success(response.message);
          this.eventEmitter.emit({ drawerClose: true, action: 'DELETE', uid: this.jobId });
          this.changeDetectorRef.markForCheck();
        }, (error: any) => {
          this.toast.close('deleting_job');
          this.changeDetectorRef.markForCheck();
        });
      }
    });
  }
  //#endregion

  //#region Assign Employee
  assignEmployeeForJob(assignedEmployees: any[]): void {
    if (assignedEmployees?.length) {
      this.jobService.taskAssignedEmployee.next(assignedEmployees);
      this.jobService.taskAssignment.next(assignedEmployees || []);
      this.changeDetectorRef.markForCheck();
    } else {
      this.jobService.taskAssignment.next([]);
      this.jobService.taskAssignedEmployee.next(null);
      this.changeDetectorRef.markForCheck();
    }
  }

  assignedEmployeeEmitHandler(event: any, tippyName: string): void {
    if (event) {
      this.tippyService.hide(tippyName);
      this.jobService.taskAssignedEmployee.next(null);
      this.jobService.taskAssignment.next([]);
      if (event !== 'close') {
        let assignmentData: any[] = [];
        if (event?.length > 0) {
          const assignedUsersData: any[] = event?.map((data: any) => ({
            type: 'ASSIGN',
            user_uid: (data?.user?.user_uid || data?.user_uid),
            team_uid: (data?.team?.team_uid || data?.team_uid)
          }));
          assignmentData = [...assignmentData, ...assignedUsersData];
          this.changeDetectorRef.markForCheck();
        }
        this.jobDetails?.assigned_to?.forEach((user: any) => {
          let userId: string = '';
          if (user?.user?.user_uid && user?.team?.team_uid) {
            userId = user.user.user_uid + '_' + user?.team?.team_uid;
          } else if (user?.user?.user_uid) {
            userId = user.user.user_uid + '_';
          }
          if (userId) {
            let isAssigned: boolean = false;
            event.forEach((assignedUser: any) => {
              let assignedUserId: string = '';
              if (assignedUser?.user?.user_uid && assignedUser?.team?.team_uid) {
                assignedUserId = assignedUser.user.user_uid + '_' + assignedUser?.team?.team_uid;
              } else if (assignedUser?.user?.user_uid) {
                assignedUserId = assignedUser.user.user_uid + '_';
              }
              if (assignedUserId && userId === assignedUserId) {
                isAssigned = true;
              }
            });
            if (!isAssigned) {
              assignmentData.push({
                type: 'UNASSIGN',
                user_uid: (user?.user?.user_uid || user?.user_uid),
                team_uid: (user?.team?.team_uid || user?.team_uid)
              });
            }
          }
        });
        if (assignmentData?.length) {
          const payload: any = {
            job: assignmentData,
            notify_users: false,
            update_all_jobs: false
          };
          this.assignOrUnassignEmployee(payload);
          this.changeDetectorRef.markForCheck();
        }
      }
      this.changeDetectorRef.markForCheck();
    }
  }

  assignOrUnassignEmployee(payLoad: any): void {
    const loader = this.toast.loading(this.utilityService.getLocaleValue('job.assgnment.updating.toast_message'));
    this.dataCollectorService.putData(this.jobService?.getJobUpdateUrl(this.jobId), payLoad).subscribe((response: any) => {
      loader.close();
      this.isJobUpdated = true;
      this.toast.success(response?.message);
      this.getJobDetails();
      this.jobUpdated.emit({ isJobUpdated: true, uid: this.jobId });
      this.changeDetectorRef.markForCheck();
    }, (error: any) => {
      loader.close();
      this.changeDetectorRef.markForCheck();
    });
  }
  //#endregion

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.jobId?.firstChange === false) {
      this.getJobDetails();
      this.changeDetectorRef.markForCheck();
    }
  }
}
